import { createTheme } from "@mui/material/styles";

import { colors } from "../overrides/colors";
import { palette } from "../overrides/palette";
import { typograhpy } from "../overrides/typography";

export const theme = createTheme({
  palette: palette,
  typography: typograhpy,
  spacing: 4,
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&:before": {
            display: "none",
          },
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontFamily: "Mulish",
          textTransform: "none",
          letterSpacing: "-0.5px",
          fontWeight: 600,
          borderRadius: ownerState.size === "large" ? "8px" : "6px",
          height:
            ownerState.size === "large"
              ? "56px"
              : ownerState.size === "medium"
              ? "44px"
              : "28px",
          padding:
            ownerState.size === "large"
              ? "16px"
              : ownerState.size === "medium"
              ? "12px"
              : "8px",
          fontSize:
            ownerState.size === "large"
              ? "18px"
              : ownerState.size === "medium"
              ? "16px"
              : "12px",
          lineHeight:
            ownerState.size === "large"
              ? "18px"
              : ownerState.size === "medium"
              ? "20.08px"
              : "15.06px",
          "&>span>svg": {
            height:
              ownerState.size === "large"
                ? "20px"
                : ownerState.size === "medium"
                ? "16px"
                : "13.33px",
            width:
              ownerState.size === "large"
                ? "20px"
                : ownerState.size === "medium"
                ? "16px"
                : "13.33px",
          },
        }),
      },
      defaultProps: {
        color: "primary",
        variant: "contained",
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 10px 30px rgba(11, 27, 38, 0.05)",
          border: "none",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          html: {
            WebkitFontSmoothing: "auto",
          },
          body: {
            backgroundColor: colors.light,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: "4px",
          backgroundColor:
            ownerState.color === "secondary"
              ? colors.theme.neutral.lightest
              : ownerState.color === "error"
              ? colors.theme.danger.lightest
              : ownerState.color === "warning"
              ? colors.theme.tertiary.lightest
              : colors.theme.primary.lightest,
          color:
            ownerState.color === "secondary"
              ? colors.theme.neutral.default
              : ownerState.color === "error"
              ? colors.theme.danger.default
              : ownerState.color === "warning"
              ? colors.theme.tertiary.default
              : colors.theme.primary.default,
          height: ownerState.size === "medium" ? "23px" : "22px",
          fontSize: ownerState.size === "medium" ? "12px" : "11px",
          fontWeight: 600,
          letterSpacing: "-0.5px",
          lineHeight: ownerState.size === "medium" ? "15.06px" : "13.81px",
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: "#F5F7FC",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorDockedLeft: {
          borderRight: "none",
          elevation: 0,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: "0px 12px 22px rgba(120, 39, 212, 0.1)",
        },
        sizeSmall: {
          boxShadow: "none",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: ownerState.size === "small" ? "6px" : "8px",
          height:
            ownerState.size === "large"
              ? "56px"
              : ownerState.size === "medium"
              ? "44px"
              : "36px",
          width:
            ownerState.size === "large"
              ? "56px"
              : ownerState.size === "medium"
              ? "44px"
              : "36px",
          padding: ownerState.size === "large" ? "8px" : "6px",
          "&>svg": {
            height: "100%",
            width: "100%",
          },
          "&:hover": {
            background: colors.theme.system.white,
            borderRadius: ownerState.size === "small" ? "6px" : "8px",
            height:
              ownerState.size === "large"
                ? "56px"
                : ownerState.size === "medium"
                ? "44px"
                : "36px",
            width:
              ownerState.size === "large"
                ? "56px"
                : ownerState.size === "medium"
                ? "44px"
                : "36px",
          },
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.theme.system.white,
          borderRadius: "8px",
          padding: "12px",
          fontSize: "16px", // Adjust font size as needed
          border: `1px solid ${colors.mapped.borderBreaks.neutral.default}`,
          fontWeight: 400,
          height: "44px",
          width: "100%",
          "&:hover": {
            border: `1px solid ${colors.mapped.borderBreaks.primary.active}`,
          },
          "&:active": {
            border: `1px solid ${colors.mapped.borderBreaks.primary.active}`,
          },
          "&:focus": {
            border: `1px solid ${colors.mapped.borderBreaks.primary.active}`,
          },
          // Styling for focus state
          "&.MuiInputBase-root": {
            "&.Mui-focused": {
              border: `1px solid ${colors.mapped.borderBreaks.primary.active}`,
            },
          },
        },
      },
      defaultProps: {
        disableUnderline: true,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.gray,
          fontWeight: 600,
          fontSize: 14,
          lineHeight: 1.6428571429,
          letterSpacing: -0.5,
          // MUI's resting label transform (translateY 16px) centers a label in
          // its natural ~56px field, but ours is forced to 44px, leaving the
          // resting label ~5px low. Nudge it to vertically center in 44px.
          // The shrunk (floated) label keeps MUI's default position.
          "&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
            transform: "translate(14px, 11px) scale(1)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: "white",
          boxShadow: "none",
          borderRadius: 0,
        },
        // The root is forced to a fixed 44px height, but MUI's default input
        // keeps 16.5px vertical padding (sized for a ~56px field), pushing the
        // text below center. Drop the vertical padding so the flex row centers
        // the text within the 44px box. Multiline keeps its padding.
        input: {
          "&:not(.MuiInputBase-inputMultiline)": {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
        notchedOutline: {
          borderColor: "#EDF0F5",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: "none",
          padding: "12px",
          "&>div:first-of-type": {
            background: "white",
            padding: 0,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: "Mulish",
          lineHeight: "17.57px",
          textTransform: "none",
          padding: 0,
          paddingBottom: 2,
          minWidth: 0,
          marginRight: "16px",
          "&>span": {
            width: "1px !important",
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          "&>.MuiTableHead-root>.MuiTableRow-root>.MuiTableCell-root": {
            backgroundColor: "transparent",
            borderBottom: "none",
            paddingTop: 0,
            paddingBottom: "12px",
            paddingRight: "20px",
            paddingLeft: "20px",
          },
          "&>.MuiTableBody-root>.MuiTableRow-root": {
            "&>.MuiTableCell-root": {
              backgroundColor: "white",
              borderBottom: "none",
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            backgroundColor: colors.theme.system.white,
            borderRadius: "8px",
          },
          // Fix the single-line height, but let multiline fields grow to fit
          // their rows instead of clipping the textarea to 44px.
          "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
            height: "44px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.mapped.borderBreaks.neutral.default,
            padding: "12px",
            fontSize: "120px",
          },
        },
      },
      defaultProps: { variant: "outlined" },
    },
  },
});
